import { useWallet } from '@solana/wallet-adapter-react'
import { useEffect, useState } from 'react'
import { EmptyBlock, ErrorBlock, Panel, Spinner } from '~/components/ui'
import {
    cancelListing,
    editPrice,
    fetchMyListings,
    requestChallenge,
    TradeError,
    TradeListing,
} from '../api/trade'

const encodeSignature = (bytes: Uint8Array): string => {
    // The server parses this with solders' `Signature.from_string`, which wants
    // base58 -- the same encoding a wallet shows the user.
    const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'
    let digits = [0]
    for (const byte of bytes) {
        let carry = byte
        for (let i = 0; i < digits.length; i += 1) {
            carry += digits[i] << 8
            digits[i] = carry % 58
            carry = (carry / 58) | 0
        }
        while (carry > 0) {
            digits.push(carry % 58)
            carry = (carry / 58) | 0
        }
    }
    let leading = ''
    for (const byte of bytes) {
        if (byte !== 0) break
        leading += '1'
    }
    return leading + digits.reverse().map((d) => ALPHABET[d]).join('')
}

export const MyListings = ({ onChanged }: { onChanged: () => void }) => {
    const { signMessage, publicKey } = useWallet()
    const [listings, setListings] = useState<TradeListing[] | null>(null)
    const [loadError, setLoadError] = useState(false)
    const [busyId, setBusyId] = useState<number | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [editing, setEditing] = useState<number | null>(null)
    const [newPrice, setNewPrice] = useState('')

    const load = () => {
        setLoadError(false)
        setListings(null)
        fetchMyListings()
            .then((data) => setListings(data.results))
            .catch(() => setLoadError(true))
    }

    useEffect(load, [])

    /** Ask for a challenge, have the wallet sign the exact message the server
     *  returned, and hand both back. The message is never rebuilt here: signing
     *  something the server did not issue is how a signature ends up covering
     *  the wrong thing. */
    const sign = async (listingId: number, action: 'cancel' | 'edit') => {
        if (!signMessage) throw new TradeError('This wallet cannot sign messages.', 'no_signer')
        const challenge = await requestChallenge(listingId, action)
        const signed = await signMessage(new TextEncoder().encode(challenge.message))
        return { nonce: challenge.nonce, signature: encodeSignature(signed) }
    }

    const cancel = async (listing: TradeListing) => {
        setBusyId(listing.id)
        setError(null)
        try {
            const { nonce, signature } = await sign(listing.id, 'cancel')
            await cancelListing(listing.id, nonce, signature)
            load()
            onChanged()
        } catch (e) {
            setError(e instanceof TradeError ? e.message : 'The wallet did not sign. Nothing changed.')
        } finally {
            setBusyId(null)
        }
    }

    const savePrice = async (listing: TradeListing) => {
        setBusyId(listing.id)
        setError(null)
        try {
            const { nonce, signature } = await sign(listing.id, 'edit')
            await editPrice(listing.id, newPrice, nonce, signature)
            setEditing(null)
            setNewPrice('')
            load()
            onChanged()
        } catch (e) {
            setError(e instanceof TradeError ? e.message : 'The wallet did not sign. Nothing changed.')
        } finally {
            setBusyId(null)
        }
    }

    if (loadError) return <ErrorBlock onRetry={load} />
    if (listings === null)
        return (
            <div className='flex justify-center py-16'>
                <Spinner visible={true} />
            </div>
        )
    if (listings.length === 0)
        return <EmptyBlock title='No active listings'>Anything you list will appear here.</EmptyBlock>

    return (
        <div className='flex flex-col gap-3'>
            {!publicKey && (
                <Panel className='p-4 text-sm text-white/70'>
                    Connect the wallet you listed from to cancel or reprice.
                </Panel>
            )}
            {error && <p className='text-xs text-[#FF6B6B]'>{error}</p>}

            {listings.map((listing) => (
                <Panel key={listing.id} className='flex flex-col gap-3 p-3 sm:flex-row sm:items-center'>
                    <div className='h-16 w-16 shrink-0 overflow-hidden bg-white/[0.03]'>
                        {listing.nft.image_url && (
                            <img
                                src={listing.nft.image_url}
                                alt={listing.nft.name}
                                className='h-full w-full object-contain'
                            />
                        )}
                    </div>

                    <div className='min-w-0 flex-1'>
                        <p className='truncate text-sm font-bold text-white'>{listing.nft.name}</p>
                        <p className='text-[11px] uppercase tracking-widest text-white/40'>
                            {listing.state_label} · ${listing.price}
                        </p>
                        {listing.review_reason && (
                            <p className='mt-1 text-xs text-[#FFC53D]'>{listing.review_reason}</p>
                        )}
                    </div>

                    {editing === listing.id ? (
                        <div className='flex items-center gap-2'>
                            <input
                                value={newPrice}
                                onChange={(e) => setNewPrice(e.target.value)}
                                inputMode='decimal'
                                placeholder='New price'
                                className='w-28 border border-white/15 bg-black/40 px-2 py-2 text-sm font-bold text-white outline-none focus:border-[#03CEFF]'
                            />
                            <button
                                onClick={() => savePrice(listing)}
                                disabled={busyId === listing.id || newPrice === ''}
                                className='min-h-[2.25rem] bg-[#03CEFF] px-3 text-xs font-black uppercase tracking-widest text-black disabled:opacity-40'
                            >
                                Save
                            </button>
                            <button
                                onClick={() => setEditing(null)}
                                className='min-h-[2.25rem] border border-white/15 px-3 text-xs font-black uppercase tracking-widest text-white/70'
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <div className='flex items-center gap-2'>
                            <button
                                onClick={() => {
                                    setEditing(listing.id)
                                    setNewPrice(listing.price)
                                }}
                                disabled={busyId === listing.id}
                                className='min-h-[2.25rem] border border-white/15 px-3 text-xs font-black uppercase tracking-widest text-white/70 hover:border-white/30 disabled:opacity-40'
                            >
                                Reprice
                            </button>
                            <button
                                onClick={() => cancel(listing)}
                                disabled={busyId === listing.id}
                                className='min-h-[2.25rem] border border-white/15 px-3 text-xs font-black uppercase tracking-widest text-white/70 hover:border-white/30 disabled:opacity-40'
                            >
                                {busyId === listing.id ? 'Signing…' : 'Take down'}
                            </button>
                        </div>
                    )}
                </Panel>
            ))}
        </div>
    )
}
